/*
 * external-checkout.js — hands the Shopify cart to the self-hosted NMI checkout.
 *
 * WHAT IT DOES
 *   Intercepts every "checkout" intent on the storefront and, instead of
 *   letting the browser go to Shopify's native /checkout, it:
 *     1. waits for any cart update still in flight (e.g. the cart drawer's
 *        Priority Shipping toggle), so the cart it reads is the one the buyer sees,
 *     2. reads the live cart (GET /cart.js),
 *     3. POSTs the line items to the checkout API (POST {ORIGIN}/api/sessions),
 *     4. redirects the browser to the returned checkout_url.
 *
 *   Shopify's cart stays the single source of truth for line items; the
 *   checkout re-prices everything server-side (Shopify prices + the CRM's
 *   quantity pricing + shipping). Client amounts are never sent or trusted.
 *
 * CONFIG
 *   The API origin comes from the `data-origin` attribute on this script's
 *   tag (Theme settings → External checkout → Checkout API origin), or from
 *   window.EXTERNAL_CHECKOUT_ORIGIN set before this file. If neither is set,
 *   checkout is BLOCKED with a console error — it never silently falls
 *   through to Shopify's /checkout.
 *
 * ROBUSTNESS
 *   One delegated listener on `document` in the CAPTURE phase catches buttons
 *   and links rendered later (cart drawer refreshes, upsell blocks) as well as
 *   those in the initial page.
 *
 * PUBLIC API
 *   window.ExternalCheckout.redirect()  -> Promise<void>  (start checkout manually)
 *   window.ExternalCheckout.origin      -> string | null  (resolved API origin)
 */
(function () {
  'use strict';

  var GLOBAL_KEY = 'ExternalCheckout';
  /** Longest we wait for in-flight cart updates before reading the cart anyway. */
  var CART_SETTLE_TIMEOUT_MS = 4000;

  // ---------------------------------------------------------------------------
  // Resolve the API origin.
  // ---------------------------------------------------------------------------
  function normalizeOrigin(value) {
    return value.replace(/\/+$/, '');
  }

  function resolveOrigin() {
    if (typeof window.EXTERNAL_CHECKOUT_ORIGIN === 'string' && window.EXTERNAL_CHECKOUT_ORIGIN.trim() !== '') {
      return normalizeOrigin(window.EXTERNAL_CHECKOUT_ORIGIN.trim());
    }
    var self =
      document.currentScript ||
      (function () {
        var scripts = document.getElementsByTagName('script');
        for (var i = scripts.length - 1; i >= 0; i--) {
          if ((scripts[i].getAttribute('src') || '').indexOf('external-checkout') !== -1) return scripts[i];
        }
        return null;
      })();
    if (self) {
      var attr = self.getAttribute('data-origin');
      if (attr && attr.trim() !== '') return normalizeOrigin(attr.trim());
    }
    return null;
  }

  var ORIGIN = resolveOrigin();

  function configError() {
    console.error(
      '[external-checkout] CHECKOUT API ORIGIN NOT CONFIGURED. Set Theme settings → External checkout → ' +
        'Checkout API origin (e.g. https://api.yourstore.com). Checkout is blocked until this is fixed.'
    );
  }

  // ---------------------------------------------------------------------------
  // In-flight cart updates. The theme adds/removes items with fetch (and a few
  // apps with XHR); a checkout click that lands mid-update would read a stale
  // cart. Count pending /cart/{add,change,update,clear} requests and let the
  // handoff wait for them to settle.
  // ---------------------------------------------------------------------------
  var pendingCartRequests = 0;
  var settleWaiters = [];

  function isCartMutation(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url || '');
  }

  function cartRequestDone() {
    pendingCartRequests = Math.max(0, pendingCartRequests - 1);
    if (pendingCartRequests === 0) {
      var waiters = settleWaiters;
      settleWaiters = [];
      for (var i = 0; i < waiters.length; i++) waiters[i]();
    }
  }

  if (typeof window.fetch === 'function') {
    var originalFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (!isCartMutation(url)) return originalFetch.apply(this, arguments);
      pendingCartRequests += 1;
      var result = originalFetch.apply(this, arguments);
      result.then(cartRequestDone, cartRequestDone);
      return result;
    };
  }

  if (window.XMLHttpRequest) {
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__ecCart = isCartMutation(String(url));
      return originalOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__ecCart) {
        pendingCartRequests += 1;
        this.addEventListener('loadend', cartRequestDone, { once: true });
      }
      return originalSend.apply(this, arguments);
    };
  }

  function cartSettled() {
    if (pendingCartRequests === 0) return Promise.resolve();
    return new Promise(function (resolve) {
      var timer = setTimeout(resolve, CART_SETTLE_TIMEOUT_MS);
      settleWaiters.push(function () {
        clearTimeout(timer);
        resolve();
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Perceived speed: warm the API connection ahead of time, and show an
  // instant full-screen loader the moment checkout is clicked.
  // ---------------------------------------------------------------------------
  function preconnect(url) {
    try {
      var pc = document.createElement('link');
      pc.rel = 'preconnect';
      pc.href = url;
      pc.crossOrigin = 'anonymous';
      (document.head || document.documentElement).appendChild(pc);
    } catch (_e) {
      /* non-fatal */
    }
  }

  function showCheckoutOverlay() {
    if (document.getElementById('external-checkout-overlay')) return;
    if (!document.getElementById('external-checkout-overlay-style')) {
      var style = document.createElement('style');
      style.id = 'external-checkout-overlay-style';
      style.textContent =
        '#external-checkout-overlay{position:fixed;inset:0;z-index:2147483647;display:flex;' +
        'flex-direction:column;align-items:center;justify-content:center;gap:16px;' +
        'background:rgba(0,0,0,0.6);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);' +
        'opacity:0;transition:opacity .15s ease}' +
        '#external-checkout-overlay.is-visible{opacity:1}' +
        '#external-checkout-overlay .ec-ovl__spinner{width:44px;height:44px;border-radius:50%;' +
        'border:3px solid rgba(255,255,255,.25);border-top-color:#fff;animation:ec-ovl-spin .7s linear infinite}' +
        '#external-checkout-overlay .ec-ovl__text{color:#fff;font-size:15px;font-weight:600;letter-spacing:.01em;' +
        'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif}' +
        '@keyframes ec-ovl-spin{to{transform:rotate(360deg)}}';
      (document.head || document.documentElement).appendChild(style);
    }
    var overlay = document.createElement('div');
    overlay.id = 'external-checkout-overlay';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.innerHTML = '<div class="ec-ovl__spinner"></div><div class="ec-ovl__text">Taking you to secure checkout…</div>';
    document.body.appendChild(overlay);
    requestAnimationFrame(function () {
      overlay.classList.add('is-visible');
    });
  }

  function hideCheckoutOverlay() {
    var overlay = document.getElementById('external-checkout-overlay');
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
  }

  // ---------------------------------------------------------------------------
  // Tracking glue. The pixels themselves are installed by the theme (layout/
  // theme.liquid); here we fire InitiateCheckout at the moment of handoff and
  // forward the pixels' first-party cookies so the checkout's server-side
  // events can be attributed to the ad click. Best-effort — tracking must
  // never block checkout.
  // ---------------------------------------------------------------------------
  function getCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : undefined;
    } catch (_e) {
      return undefined;
    }
  }

  function trackInitiateCheckout(cart) {
    try {
      var items = cart.items || [];
      var data = {
        content_type: 'product',
        content_ids: items.map(function (i) {
          return String(i.variant_id);
        }),
        contents: items.map(function (i) {
          return { id: String(i.variant_id), quantity: i.quantity, item_price: (i.final_price || i.price || 0) / 100 };
        }),
        value: (cart.total_price || 0) / 100,
        currency: cart.currency || 'USD',
        num_items: cart.item_count || items.length,
      };
      if (window.TrackEvents && typeof window.TrackEvents.send === 'function') {
        window.TrackEvents.send('InitiateCheckout', data);
      }
    } catch (_e) {
      /* never block checkout on tracking */
    }
  }

  // ---------------------------------------------------------------------------
  // Core redirect flow.
  // ---------------------------------------------------------------------------
  var inFlight = false;

  function redirect() {
    if (!ORIGIN) {
      configError();
      return Promise.reject(new Error('external-checkout: API origin not configured'));
    }
    if (inFlight) return Promise.resolve();
    inFlight = true;
    showCheckoutOverlay();

    return cartSettled()
      .then(function () {
        return fetch('/cart.js', { method: 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      })
      .then(function (res) {
        if (!res.ok) throw new Error('external-checkout: /cart.js responded ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        var items = (cart && Array.isArray(cart.items) ? cart.items : []).map(function (i) {
          return { variant_id: i.variant_id, quantity: i.quantity, properties: i.properties || null };
        });
        if (items.length === 0) throw new Error('external-checkout: cart is empty');

        trackInitiateCheckout(cart);

        return fetch(ORIGIN + '/api/sessions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          mode: 'cors',
          // Pixel cookies for server-side attribution (undefined keys are dropped
          // by JSON.stringify — nothing is sent when absent).
          body: JSON.stringify({
            items: items,
            fbp: getCookie('_fbp'),
            fbc: getCookie('_fbc'),
            ttp: getCookie('_ttp'),
            ttclid: getCookie('ttclid'),
          }),
        });
      })
      .then(function (res) {
        if (!res.ok) {
          return res.text().then(function (body) {
            throw new Error('external-checkout: POST /api/sessions responded ' + res.status + ' ' + body);
          });
        }
        return res.json();
      })
      .then(function (data) {
        if (!data || typeof data.checkout_url !== 'string' || data.checkout_url === '') {
          throw new Error('external-checkout: response missing checkout_url');
        }
        window.location.assign(data.checkout_url);
      })
      .catch(function (err) {
        inFlight = false;
        hideCheckoutOverlay();
        console.error('[external-checkout] checkout failed:', err);
        try {
          window.alert('Sorry — we could not start checkout. Please try again in a moment.');
        } catch (_e) {
          /* alert unavailable — the console error above stands */
        }
        throw err;
      });
    // On success `inFlight` stays true: the page is navigating away and must
    // not create a second session.
  }

  // ---------------------------------------------------------------------------
  // Interception. Single delegated listener, capture phase, on `document`.
  // ---------------------------------------------------------------------------
  // /checkout, /checkouts/…, and market-prefixed forms like /en-ca/checkout.
  var CHECKOUT_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2,4})?\/)?checkouts?(?:[/?#]|$)/i;

  function isCheckoutUrl(url) {
    return url.host === window.location.host && CHECKOUT_PATH.test(url.pathname);
  }

  function isCheckoutHref(href) {
    if (!href) return false;
    try {
      return isCheckoutUrl(new URL(href, window.location.href));
    } catch (_e) {
      return false;
    }
  }

  function matchTrigger(target) {
    var el = target;
    while (el && el !== document && el.nodeType === 1) {
      // Cart page / cart drawer / cart notification checkout buttons.
      if (el.matches('button[name="checkout"], input[name="checkout"], #CartDrawer-Checkout')) return el;
      if (el.tagName === 'A' && isCheckoutHref(el.getAttribute('href') || '')) return el;
      el = el.parentElement;
    }
    return null;
  }

  function onClickCapture(event) {
    if (event.defaultPrevented) return;
    var trigger = matchTrigger(event.target);
    if (!trigger) return;
    if (trigger.disabled) return;
    // Intercept ANY click — including middle/modified clicks — so a /checkout
    // link can never open Shopify's checkout in a new tab.
    event.preventDefault();
    event.stopPropagation();
    redirect().catch(function () {
      /* already logged + alerted */
    });
  }

  function onSubmitCapture(event) {
    if (event.defaultPrevented) return;
    var form = event.target;
    if (!form || form.nodeType !== 1) return;
    var action = form.getAttribute('action') || '';
    var submitter = event.submitter || null;
    var submitterIsCheckout = submitter && submitter.matches && submitter.matches('[name="checkout"]');
    var isCartForm = /\/cart\b/.test(action) && !/\/cart\/add/.test(action);
    var hasCheckoutSubmit = !!form.querySelector('[name="checkout"]');
    // A cart form submitted by its checkout button (or by Enter with one present).
    if (submitterIsCheckout || (isCartForm && hasCheckoutSubmit && (!submitter || submitterIsCheckout))) {
      event.preventDefault();
      event.stopPropagation();
      redirect().catch(function () {
        /* handled in redirect() */
      });
    }
  }

  document.addEventListener('click', onClickCapture, true);
  document.addEventListener('submit', onSubmitCapture, true);

  // Scripted navigations: theme or app code that does
  // `location.href = '/checkout…'` (often minified or obfuscated, so it can't
  // be patched in the theme). The Navigation API reports every navigation the
  // page starts, whatever triggered it, and lets us cancel it. Browsers without
  // it rely on the click/submit interception above.
  if (window.navigation && typeof window.navigation.addEventListener === 'function') {
    window.navigation.addEventListener('navigate', function (event) {
      if (!event.cancelable || event.hashChange || event.downloadRequest) return;
      var url;
      try {
        url = new URL(event.destination.url);
      } catch (_e) {
        return;
      }
      if (!isCheckoutUrl(url)) return;
      event.preventDefault();
      redirect().catch(function () {
        /* handled in redirect() */
      });
    });
  }

  // Back from the checkout restores this page from the bfcache with
  // `inFlight` still true; reset it (and any leftover loader) on every show.
  window.addEventListener('pageshow', function () {
    inFlight = false;
    hideCheckoutOverlay();
  });

  // ---------------------------------------------------------------------------
  // Defense in depth: hide Shopify's dynamic/express checkout buttons (Shop
  // Pay, Apple Pay, Google Pay, PayPal, "Buy it now"). They launch Shopify's
  // own express checkout, which this script cannot intercept. Removing
  // `{{ form | payment_button }}` from the theme is the primary fix; this
  // hides any an app might inject.
  // ---------------------------------------------------------------------------
  (function hideExpressButtons() {
    if (document.getElementById('external-checkout-hide-express')) return;
    var style = document.createElement('style');
    style.id = 'external-checkout-hide-express';
    style.textContent =
      '.shopify-payment-button,.dynamic-checkout__content,shopify-accelerated-checkout,shopify-accelerated-checkout-cart,' +
      '[data-shopify="dynamic-checkout-cart"],[data-shopify="payment-button"]{display:none !important}';
    (document.head || document.documentElement).appendChild(style);
  })();

  // ---------------------------------------------------------------------------
  // Public API.
  // ---------------------------------------------------------------------------
  window[GLOBAL_KEY] = { redirect: redirect, origin: ORIGIN };

  if (!ORIGIN) configError();
  else preconnect(ORIGIN);
})();
